import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Button, DialogTitle, Stack, TextField, Typography } from '@mui/material';

import { patchEndorsementAtom } from '@/interfaces/endorsement';

import type { EndorsementData } from '@/@types/site';

interface EditEndorsementDialogProps {
  onClose: (reason?: string) => void;
  endorsement: EndorsementData;
}

export default function EditEndorsementDialog({ onClose, endorsement }: EditEndorsementDialogProps) {
  const { mutate, isPending, data: isSuccess } = useAtomValue(patchEndorsementAtom);

  const [name, setName] = useState(endorsement.name);
  const [description, setDescription] = useState(endorsement.description);
  const { t } = useTranslation();

  const nameTooLong = name.length > 20;
  const descriptionTooLong = description.length > 20;
  const nameErrorText = nameTooLong ? t('FDA.message.nameTooLong') : '';
  const descriptionErrorText = descriptionTooLong ? t('FDA.message.descriptionTooLong') : '';
  const notValid = name.length === 0 || description.length === 0 || nameTooLong || descriptionTooLong || isPending;

  useEffect(() => {
    if (isSuccess === undefined) return;
    if (isSuccess) {
      onClose('updated');
    } else {
      window.alert(t('Common.message.serverErrorOccured'));
    }
  }, [isSuccess, onClose, t]);

  function onOkClick() {
    mutate({ ...endorsement, name, description });
  }

  return (
    <Stack gap={2} sx={{ minWidth: 480, p: 3 }}>
      <DialogTitle sx={{ px: 0 }}>{t('FDA.label.editEndorsement')}</DialogTitle>
      <Typography>{t('Common.label.name')}</Typography>
      <TextField
        value={name}
        error={nameTooLong}
        helperText={nameErrorText}
        onChange={(e) => setName(e.target.value)}
      />
      <Typography>{t('Common.label.description')}</Typography>
      <TextField
        value={description}
        error={descriptionTooLong}
        helperText={descriptionErrorText}
        onChange={(e) => setDescription(e.target.value)}
      />

      <Stack direction="row" justifyContent="center" gap={3}>
        <Button variant="contained" onClick={() => onClose()} sx={{ bgcolor: '#CCC', color: 'black' }}>
          {t('Common.label.cancel')}
        </Button>
        <Button variant="contained" disabled={notValid} onClick={onOkClick} sx={{ bgcolor: 'info.main' }}>
          {t('Common.label.ok')}
        </Button>
      </Stack>
    </Stack>
  );
}
